import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from '@/lib/core/utils';
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SourceDataPoint } from '@/types/index'; // Assuming SourceDataPoint is defined here

interface FilterControlsProps {
  startDate: Date | undefined;
  setStartDate: (date: Date | undefined) => void;
  endDate: Date | undefined;
  setEndDate: (date: Date | undefined) => void;
  selectedSource: string | "all";
  setSelectedSource: (source: string | "all") => void;
  reviewSourcesData: SourceDataPoint[]; // Need this for the source dropdown
  selectedTag: string | "all";
  setSelectedTag: (tag: string | "all") => void;
  uniqueTags: string[];
  selectedSentiment: string | "all";
  setSelectedSentiment: (sentiment: string | "all") => void;
  sortOption: string;
  setSortOption: (sortOption: string) => void;
}

const FilterControls: React.FC<FilterControlsProps> = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedSource,
  setSelectedSource,
  reviewSourcesData,
  selectedTag,
  setSelectedTag,
  uniqueTags,
  selectedSentiment,
  setSelectedSentiment,
  sortOption,
  setSortOption,
}) => {
  return (
    <div className="flex flex-wrap gap-2 items-center mb-4">
      <p className="dark:text-white"> Order: </p> {/* Added dark mode text color */}
      <Select onValueChange={setSortOption} value={sortOption}>
        <SelectTrigger className={cn("w-full sm:w-[180px]", sortOption !== "newest" && "border-2 border-orange-500")} data-filter-active={sortOption !== "newest" ? true : undefined}>
          <SelectValue placeholder="Sort By" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="newest">Newest</SelectItem>
          <SelectItem value="oldest">Oldest</SelectItem>
          <SelectItem value="most_stars">Most Stars</SelectItem>
          <SelectItem value="least_stars">Least Stars</SelectItem>
        </SelectContent>
      </Select>
      <p className="dark:text-white"> Filter: </p> {/* Added dark mode text color */}
      {/* Date Picker */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            className={cn(
              "w-full sm:w-[240px] justify-start text-left font-normal",
              (!startDate && !endDate) && "text-muted-foreground",
              (startDate || endDate) && "border-2 border-orange-500"
            )}
            data-filter-active={startDate || endDate ? true : undefined}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {startDate ? (
              endDate ? (
                `${format(startDate, "LLL dd, y")} - ${format(endDate, "LLL dd, y")}`
              ) : (
                format(startDate, "LLL dd, y")
              )
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={{ from: startDate, to: endDate }}
            onSelect={(range) => {
              setStartDate(range?.from);
              setEndDate(range?.to);
            }}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>

      {/* Source Filter */}
      <Select onValueChange={setSelectedSource} value={selectedSource}>
        <SelectTrigger className={cn("w-full sm:w-[180px]", selectedSource !== "all" && "border-2 border-orange-500")} data-filter-active={selectedSource !== "all" ? true : undefined}>
          <SelectValue placeholder="Select Source" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Sources</SelectItem>
          {/* Map through unique sources from reviewSourcesData */}
          {reviewSourcesData.map((source) => (
            <SelectItem key={source.name} value={source.name}>
              {source.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Tag Filter */}
      <Select onValueChange={setSelectedTag} value={selectedTag}>
        <SelectTrigger className={cn("w-full sm:w-[180px]", selectedTag !== "all" && "border-2 border-orange-500")} data-filter-active={selectedTag !== "all" ? true : undefined}>
          <SelectValue placeholder="Select Tag" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Tags</SelectItem>
          {uniqueTags.filter(tag => tag !== "all").map((tag) => (
            <SelectItem key={tag} value={tag}>
              {tag}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Sentiment Filter */}
      <Select onValueChange={setSelectedSentiment} value={selectedSentiment}>
        <SelectTrigger className={cn("w-full sm:w-[180px]", selectedSentiment !== "all" && "border-2 border-orange-500")} data-filter-active={selectedSentiment !== "all" ? true : undefined}>
          <SelectValue placeholder="Select Sentiment" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Sentiments</SelectItem>
          <SelectItem value="positive">Positive</SelectItem>
          <SelectItem value="neutral">Neutral</SelectItem>
          <SelectItem value="negative">Negative</SelectItem>
        </SelectContent>
      </Select>

      {/* Clear Filters Button */}
      {(startDate || endDate || selectedSource !== "all" || selectedTag !== "all" || selectedSentiment !== "all" || sortOption !== "newest") && (
        <Button
          variant="outline"
          className="dark:text-white w-full sm:w-auto"
          onClick={() => {
            setStartDate(undefined);
            setEndDate(undefined);
            setSelectedSource("all");
            setSelectedTag("all");
            setSelectedSentiment("all");
            setSortOption("newest"); // Also clear sort option
          }}
        >
          Clear Filters
        </Button>
      )}
    </div>
  );
};

export default FilterControls;
